import { useCallback, useState } from 'react';

import { ICartEntry } from '@rbi-ctg/menu';
import {
  IBenefitSwap,
  ILoyaltyOffersQuery,
  ILoyaltyUserOffersQuery,
  IOffersFragment,
  IPromotionInput,
  OfferRedemptionType,
  PromotionType,
  SwapType,
  useLoyaltyOffersLazyQuery,
  useLoyaltyUserIncentiveEvaluationLazyQuery,
  useLoyaltyUserOffersLazyQuery,
} from 'generated/graphql-gateway';
import { IAppliedOffer, ISwap } from 'generated/rbi-graphql';
import { ISystemwideOffersFragment } from 'generated/sanity-graphql';
import { useLocalStorageState } from 'hooks/use-local-storage-state';
import { LaunchDarklyFlag, useFlag } from 'state/launchdarkly';
import { useServiceModeContext } from 'state/service-mode';
import { useStoreContext } from 'state/store';
import LocalStorage, { StorageKeys } from 'utils/local-storage';
import { parseStringifiedJSON } from 'utils/parse-string';

import {
  IAppliedRewards,
  ICartEntryAdapter,
  IQueryLoyaltyUserOffersOptions,
  LoyaltyOfferFeedbackMap,
  isSwap,
} from './types';

const getAppliedOffersFromStorage = () => {
  const storedAppliedOffers = LocalStorage.getItem(StorageKeys.APPLIED_LOYALTY_OFFERS);
  return parseStringifiedJSON({ value: storedAppliedOffers, defaultValue: [] }) || [];
};

export const updateAppliedOffersInStorage = (appliedOffers: IAppliedOffer[]) => {
  LocalStorage.setItem(StorageKeys.APPLIED_LOYALTY_OFFERS, JSON.stringify(appliedOffers));
};

export const removeAppliedOffersInStorage = () => {
  LocalStorage.removeItem(StorageKeys.APPLIED_LOYALTY_OFFERS);
};

interface IEntriesIdsMap {
  [key: string]: boolean;
}
// Creates a flat map of all cart entries with sanityId property as key
const flattenEntriesToMap: (acc: IEntriesIdsMap, entry: ICartEntryAdapter) => IEntriesIdsMap = (
  acc: IEntriesIdsMap,
  entry: ICartEntryAdapter
) => ({ ...acc, [entry.sanityId]: true, ...entry.children?.reduce(flattenEntriesToMap, {}) });

// Function maps from ICartEntry to ICartEntryAdapter to send entries to BE
// Also filters entries that have a reward applied
const parseEntry = (appliedLoyaltyRewards?: IAppliedRewards | null) => (
  acc: ICartEntryAdapter[],
  { _id, cartId, price = 0, quantity = 1, children }: ICartEntry
) => {
  const rewardApplied = appliedLoyaltyRewards?.[cartId];
  if (rewardApplied?.timesApplied === quantity) {
    return acc;
  }

  const parsed: ICartEntryAdapter = {
    sanityId: _id,
    lineId: cartId,
    price,
    quantity,
  };
  if (children.length) {
    parsed.children = children.reduce(parseEntry(appliedLoyaltyRewards), []);
  }
  return [...acc, parsed];
};

export const useLoyaltyOffers = () => {
  const loyaltyOffersEnabled = Boolean(useFlag(LaunchDarklyFlag.ENABLE_LOYALTY_OFFERS));
  const { serviceMode } = useServiceModeContext();
  const { store } = useStoreContext();

  const storeId = store?.number;

  const [upsizeAvailable, setUpsizeAvailable] = useState(false);
  const [surpriseAvailable, setSurpriseAvailable] = useState(false);
  const [offers, setOffers] = useState<IOffersFragment[]>([]);
  const [userOffers, setUserOffers] = useState<IOffersFragment[]>([]);
  const [appliedOffers, setAppliedOffers] = useState(getAppliedOffersFromStorage);
  const [cartEntriesIdsMap, setCartEntriesIdsMap] = useState<IEntriesIdsMap>({});
  const [
    selectedOffer,
    setSelectedOffer,
    clearSelectedOffer,
  ] = useLocalStorageState<ISystemwideOffersFragment | null>({
    key: StorageKeys.SELECTED_LOYALTY_OFFER,
    defaultReturnValue: null,
  });
  const [offersFeedbackMap, setOffersFeedbackMap] = useState<LoyaltyOfferFeedbackMap>({});

  const onUserOffersQueryComplete = (data: ILoyaltyUserOffersQuery) => {
    const loyaltyUserOffers = data?.loyaltyUser?.offers;
    if (loyaltyUserOffers?.length) {
      const validOffers = loyaltyUserOffers.map(offer => {
        const isValidSwapOffer = (singleBenefit: IBenefitSwap) => {
          // Checking swap from value is in cartEntries
          const isValidSwap = cartEntriesIdsMap[singleBenefit.value.from];

          if (isValidSwap) {
            setUpsizeAvailable(true);
          }
          return isValidSwap;
        };

        // Filtering valid benefits from offer
        const benefits = offer?.benefits?.filter(benefit => {
          if (isSwap(benefit)) {
            return isValidSwapOffer(benefit);
          }
          // Offer is not a swap so don't filter it
          return true;
        });

        return { ...offer, benefits };
      });

      setUserOffers(validOffers as IOffersFragment[]);

      // Only show modal if there is any surprise offer and if it is not already applied
      const surpriseApplied = appliedOffers.some((appliedOffer: any) => {
        const offer = loyaltyUserOffers.find(userOffer => userOffer.id === appliedOffer.id);
        return offer?.redemptionType === OfferRedemptionType.SURPRISE;
      });

      if (surpriseApplied) {
        setSurpriseAvailable(false);
      } else {
        const validSurpriseAvailable = validOffers.some(
          offer => offer.redemptionType === OfferRedemptionType.SURPRISE
        );
        setSurpriseAvailable(Boolean(validSurpriseAvailable));
      }
    }
  };

  const [queryUserOffers] = useLoyaltyUserOffersLazyQuery({
    fetchPolicy: 'no-cache',
    onCompleted: onUserOffersQueryComplete,
  });

  const [queryEngineOffers] = useLoyaltyOffersLazyQuery({
    fetchPolicy: 'network-only',
    variables: {
      serviceMode: serviceMode || undefined,
      storeId,
      omitInvalids: false,
    },
    onCompleted: (data: ILoyaltyOffersQuery) => {
      if (data?.loyaltyOffers?.length) {
        setOffers(data.loyaltyOffers as IOffersFragment[]);
      }
    },
  });

  const parseOffers = useCallback(
    (appliedLoyaltyOffers: IAppliedOffer[]) =>
      appliedLoyaltyOffers.reduce(
        (acc: IPromotionInput[], { cartId: lineId, id, swap }: IAppliedOffer) => {
          if (id) {
            acc.push({
              ...(swap && { swap: parseSwap(swap) }),
              id,
              lineId,
              type: PromotionType.OFFER,
            });
          }

          return acc;
        },
        []
      ),
    []
  );

  const parseSwap = ({ cartId: lineId, to, from, swapType }: ISwap) => ({
    to,
    from,
    lineId,
    type: swapType as SwapType,
  });

  const queryLoyaltyUserOffers = useCallback(
    ({
      loyaltyId,
      cartEntries,
      appliedRewards,
      subtotalAmount,
      redemptionTypes,
    }: IQueryLoyaltyUserOffersOptions) => {
      const parsedEntries = cartEntries?.reduce(parseEntry(appliedRewards), []);
      setCartEntriesIdsMap((parsedEntries || []).reduce(flattenEntriesToMap, {}));
      queryUserOffers({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedOffers),
            cartEntries: parsedEntries,
            serviceMode: serviceMode || undefined,
            redemptionTypes,
            storeId,
            subtotalAmount,
            omitInvalids: false,
          },
        },
      });
    },
    [queryUserOffers, parseOffers, appliedOffers, serviceMode, storeId]
  );

  const queryLoyaltyOffers = useCallback(
    ({ subtotalAmount = 0 }) => {
      queryEngineOffers({ variables: { serviceMode, storeId, subtotalAmount } });
    },
    [queryEngineOffers, serviceMode, storeId]
  );

  const [evaluateLoyaltyIncentives] = useLoyaltyUserIncentiveEvaluationLazyQuery({
    fetchPolicy: 'network-only',
    onCompleted: ({ loyaltyUser }) => {
      if (loyaltyUser?.evaluatedIncentives?.invalidOffers) {
        const offerFeedbackMap = loyaltyUser.evaluatedIncentives.invalidOffers.reduce(
          (acc, feedback) => {
            acc[feedback.id] = feedback.errors;
            return acc;
          },
          {}
        );
        setOffersFeedbackMap(offerFeedbackMap);
      }
    },
  });

  const evaluateLoyaltyUserIncentives = useCallback(
    (
      loyaltyId: string,
      cartEntries?: ICartEntry[],
      appliedLoyaltyRewards?: IAppliedRewards | null,
      subtotalAmount?: number
    ) => {
      const parsedEntries = cartEntries?.reduce(parseEntry(appliedLoyaltyRewards), []);
      setCartEntriesIdsMap((parsedEntries || []).reduce(flattenEntriesToMap, {}));

      evaluateLoyaltyIncentives({
        variables: {
          loyaltyId,
          where: {
            appliedIncentives: parseOffers(appliedOffers),
            cartEntries: parsedEntries,
            serviceMode: serviceMode || undefined,
            storeId,
            subtotalAmount,
          },
        },
      });
    },
    [evaluateLoyaltyIncentives, parseOffers, appliedOffers, serviceMode, storeId]
  );

  const resetUpsizeAvailability = () => setUpsizeAvailable(false);
  const resetSurpriseAvailability = () => setSurpriseAvailable(false);

  const resetAppliedOffers = () => {
    updateAppliedOffersInStorage([]);
    setAppliedOffers([]);
  };

  const updateAppliedOffers = (cartEntry: ICartEntry) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = oldOffers.filter(offer => {
        if (cartEntry.cartId === offer.cartId && offer.id === selectedOffer?.loyaltyEngineId) {
          clearSelectedOffer();
        }
        // If cart entry has children, cartIds could match
        // otherwise, cartId should check against Swap cartId
        return offer?.swap && !cartEntry.children.length
          ? offer?.swap?.cartId !== cartEntry?.cartId
          : offer?.cartId !== cartEntry?.cartId;
      });
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };

  const applyOffer = (newOffer: IAppliedOffer) => {
    setAppliedOffers((oldOffers: IAppliedOffer[]) => {
      const updatedOffers = selectedOffer ? [newOffer] : oldOffers.concat(newOffer);
      updateAppliedOffersInStorage(updatedOffers);

      return updatedOffers;
    });
  };
  return {
    appliedOffers,
    queryLoyaltyUserOffers,
    queryLoyaltyOffers,
    userOffers,
    loyaltyOffersEnabled,
    offers,
    resetUpsizeAvailability,
    resetSurpriseAvailability,
    setAppliedOffers,
    upsizeAvailable,
    surpriseAvailable,
    applyOffer,
    updateAppliedOffers,
    resetAppliedOffers,
    selectedOffer,
    setSelectedOffer,
    evaluateLoyaltyUserIncentives,
    offersFeedbackMap,
    setOffersFeedbackMap,
  };
};
